import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield, RefreshCw, Activity, User, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const SecurityEvents = () => {
  const [data, setData] = useState({ total: 0, today: 0, events: [] });
  const load = () => api.get("/security/events").then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="bg-white rounded-2xl border-2 border-red-200 shadow-sm overflow-hidden" data-testid="security-events-card">
      <div className="px-5 py-4 bg-red-50 border-b border-red-200 flex items-center justify-between">
        <div className="flex items-center gap-2 font-heading font-bold text-red-800">
          <ShieldAlert className="h-5 w-5" /> Tentatives d&apos;accès refusées (403)
        </div>
        <div className="text-xs text-red-700 font-semibold">
          Aujourd&apos;hui : <strong data-testid="security-today-count">{data.today}</strong> · Total : {data.total}
        </div>
      </div>
      <div className="max-h-[360px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2">Date/heure</th>
              <th className="px-4 py-2">Auteur</th>
              <th className="px-4 py-2">Rôle</th>
              <th className="px-4 py-2">Action tentée</th>
              <th className="px-4 py-2">Motif</th>
              <th className="px-4 py-2">IP</th>
            </tr>
          </thead>
          <tbody>
            {data.events.map((e, i) => (
              <tr key={i} className="border-t border-slate-100 hover:bg-red-50/40" data-testid={`security-event-${i}`}>
                <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{new Date(e.at).toLocaleString("fr-FR")}</td>
                <td className="px-4 py-2 text-slate-700">{e.full_name || e.email || "inconnu"}</td>
                <td className="px-4 py-2"><span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded font-bold uppercase">{e.role || "?"}</span></td>
                <td className="px-4 py-2 font-mono text-xs text-red-700">{e.method} {e.path}</td>
                <td className="px-4 py-2 text-xs text-slate-500 max-w-[220px] truncate" title={e.detail}>{e.detail}</td>
                <td className="px-4 py-2 text-xs text-slate-400">{e.ip}</td>
              </tr>
            ))}
            {data.events.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Aucune tentative refusée — tout est en ordre ✓</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-2.5 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500">
        Chaque refus est bloqué par le serveur, tracé ici, et le Super Admin est alerté par email (max 1 alerte / auteur / 30 min).
      </div>
    </div>
  );
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try {
      const [{ data: ls }, { data: st }] = await Promise.all([
        api.get("/audit/logs", { params: filter ? { action: filter } : {} }),
        api.get("/audit/stats"),
      ]);
      setLogs(ls); setStats(st);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <div className="chip mb-2"><Shield className="h-3 w-3" /><span>SÉCURITÉ — AUDIT</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Journal d&apos;audit</h1>
        <p className="text-slate-500 mt-1">{stats?.total || 0} événements enregistrés. Traçabilité de toutes les actions sensibles.</p>
      </div>

      <SecurityEvents />

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="font-heading font-bold text-lg text-[#1E3A8A] mb-3 flex items-center gap-2"><Activity className="h-5 w-5" />Actions les plus fréquentes</h2>
            <div className="space-y-2">
              {stats.top_actions.map(a => (
                <div key={a.action} className="flex items-center justify-between text-sm">
                  <span className="font-mono text-slate-700">{a.action}</span>
                  <span className="tabular-nums font-bold text-[#1E3A8A]">{a.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="font-heading font-bold text-lg text-[#1E3A8A] mb-3 flex items-center gap-2"><User className="h-5 w-5" />Utilisateurs les plus actifs</h2>
            <div className="space-y-2">
              {stats.top_users.map(u => (
                <div key={u.user_email} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{u.user_email}</span>
                  <span className="tabular-nums font-bold text-[#1E3A8A]">{u.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Input placeholder="Filtrer par action..." value={filter} onChange={e => setFilter(e.target.value)} className="max-w-xs" data-testid="filter" />
        <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-2" />Actualiser</Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Date/heure</th>
                <th className="px-4 py-3">Utilisateur</th>
                <th className="px-4 py-3">Rôle</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Entité</th>
                <th className="px-4 py-3">Détails</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`log-${l.id}`}>
                  <td className="px-4 py-2 text-xs text-slate-500">{new Date(l.created_at).toLocaleString("fr-FR")}</td>
                  <td className="px-4 py-2 text-slate-700">{l.user_email}</td>
                  <td className="px-4 py-2"><span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded font-bold uppercase">{l.user_role}</span></td>
                  <td className="px-4 py-2 font-mono text-xs text-[#1E3A8A] font-bold">{l.action}</td>
                  <td className="px-4 py-2 text-xs">{l.entity} <code className="text-slate-400">{l.entity_id?.slice(0, 8)}</code></td>
                  <td className="px-4 py-2 text-xs text-slate-500 max-w-xs truncate">{JSON.stringify(l.details)}</td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">Aucun événement d&apos;audit pour ce filtre.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
